<template>
    <div>
        <div style="margin-right: 40px;">
            <md-field>
                <span v-show="activePanel.name.trim().length > 0" style="color: rgba(0,0,0,0.54); font-size: 12px; position: absolute; top: -5px;">
                    Panel Name
                </span>
                <vue-simple-suggest v-model="activePanel.name" placeholder="Panel Name - HCPCS" @input="(e) => {activePanel.name = e; if(Object.keys(costData).includes(e)){activePanel.cost = costData[e].cost.toFixed(2)}}"
                    :list="Object.keys(costData)" :max-suggestions=1000 :min-length=2 :filter-by-query="true">
                    <template slot="misc-item-above" slot-scope="{ suggestions, query }">
                        <!-- Sub-template if have any suggestions -->
                        <template v-if="suggestions.length > 0">
                            <div class="misc-item" style="margin-top: 10px;">
                                <span>{{ suggestions.length }} suggestions are shown for '{{ query }}'</span>
                            </div>
                            <hr>
                        </template>
                        <!-- Show "No result" otherwise -->
                        <div class="misc-item" v-else>
                            <span><a target="_blank" :href="constructHcpcsSearch(query)">
                                    No results found, search for HCPCS code?
                                </a></span>
                        </div>
                    </template>
                </vue-simple-suggest>
            </md-field>
            <md-field>
                <label>Panel Cost (in USD)</label>
                <md-input v-model="activePanel.cost" type="number"></md-input>
            </md-field>
        </div>
        <!-- Labs table editor -->
        <div>
            <hr>
            <h2 style="text-align:center; margin: 25px 0;">
                Test Results
            </h2>
            <md-table md-card class="lab-table">
                <md-table-row>
                    <md-table-head>Measurement</md-table-head>
                    <md-table-head>Reference Range</md-table-head>
                    <md-table-head>Measured Value</md-table-head>
                    <md-table-head>Flag</md-table-head>
                    <md-table-head>Delete</md-table-head>
                </md-table-row>
                <md-table-row v-for="(result, resultIndex) in activePanel.results" :key="result.id">
                    <md-table-cell>
                        <md-field>
                            <md-textarea v-model="result.name" @paste="onPaste(resultIndex)" md-autogrow></md-textarea>
                        </md-field>
                    </md-table-cell>
                    <md-table-cell>
                        <md-field>
                            <md-input v-model="result.refRange"></md-input>
                        </md-field>
                    </md-table-cell>
                    <md-table-cell>
                        <md-field>
                            <md-input v-model="result.value"></md-input>
                        </md-field>
                    </md-table-cell>
                    <md-table-cell>
                        <div class="flag-cell" :style="{'background-color': calcFlagColor(result)}">
                            <b>{{calcFlag(result)}}</b>
                            <md-tooltip v-if="calcFlag(result) == '?'">
                                <div style="margin-top: 6px; line-height: 1">
                                    Recognizes numeric reference ranges
                                    <br>
                                    <sub>(3.7-5.1 and &lt;0.3 or &gt;0.3)</sub>
                                </div>
                                <div>OR</div>
                                <div>Recognizes when reference range equals value</div>
                            </md-tooltip>
                        </div>
                    </md-table-cell>
                    <md-table-cell>
                        <span @click="deletePanelResult(resultIndex)">
                            <md-icon class="md-accent">cancel</md-icon>
                        </span>
                    </md-table-cell>
                </md-table-row>
            </md-table>
            <md-button class="md-primary md-raised" @click="addPanelResult()" style="margin-top: 20px">
                Add Measurement
            </md-button>
            <br><br>
            <i>Note: You may paste tab-separated tables (copied from Excel, Word, Google Drive, etc.) into the first cell in
                a table row, for automatic entry.</i>
            <br>
        </div>
        <hr>
        <quill-editor v-model="activePanel.info.corpus" :options="{'placeholder': 'Supplementary results/info here (optional)'}">
        </quill-editor>
        <ResourceGallery :resourceList="activePanel.info.resources" v-on:saveSlide="saveSlide(false)"></ResourceGallery>
        <div v-if="!presetMode">
            <hr><br><br>
            <center>
                <b>Flip the switch below if this panel is necessary to reach the correct diagnosis:</b>
                <br>
                <md-switch class="md-primary" v-model="activePanel.should_order" @change="activePanel.should_not_order = activePanel.should_order ? false : activePanel.should_not_order; saveSlide(false);">
                    Necessary Lab
                </md-switch>
                <br><br>
                <b>Flip the switch below if this panel is of low value in this case:</b>
                <br>
                <md-switch class="md-primary" v-model="activePanel.should_not_order" @change="activePanel.should_order = activePanel.should_not_order ? false : activePanel.should_order; saveSlide(false);">
                    Low Value Lab
                </md-switch>
            </center>
        </div>
    </div>
</template>

<script>
    import ExcelImport from './ExcelImport.js'
    import ResourceGallery from './Gallery.vue'
    import VueSimpleSuggest from 'vue-simple-suggest'
    import 'vue-simple-suggest/dist/styles.css' // Optional CSS
    const ObjectID = require("bson-objectid");

    export default {
        props: {
            activePanel: {
                type: Object
            },
            costData: {
                type: Object
            },
            presetMode: {
                type: Boolean
            },
        },
        components: {
            ResourceGallery,
            VueSimpleSuggest,
        },
        methods: {
            saveSlide: function(closeSlide) {
                this.$emit("saveSlide", closeSlide)
            },
            calcFlagColor: function(result){
                let flagName = this.calcFlag(result)
                if(flagName ==  "?"){
                    return "#f8fa63"
                } 
                if(flagName == ""){
                    return "inherit"
                }
                return "#fd8585"    
            },
            calcFlag: function(result){
                if(result.refRange == result.value){
                    return ""
                }
                if(isNaN(parseFloat(result.value))){
                    return "?"
                }
                let refRangeRegex = /([+-]?\d*(?:.\d+)?)\s?[-\u2013\u2014]\s?([+-]?\d*(?:.\d+)?)/
                let regexResult = result.refRange.match(refRangeRegex)
                if(regexResult) {
                    let lowBoundary = parseFloat(regexResult[1])
                    let highBoundary = parseFloat(regexResult[2])
                    if(highBoundary < lowBoundary){
                        lowBoundary = highBoundary
                        highBoundary = parseFloat(regexResult[1])
                    }
                    let normalFlag = result.value <= highBoundary && result.value >= lowBoundary
                    if(!normalFlag){
                        if(result.value > highBoundary) {
                            return "HIGH"
                        }
                        if(result.value < lowBoundary) {
                            return "LOW"
                        }
                    }
                    else {
                        return ""
                    }
                }
                refRangeRegex = /<\s?([+-]?\d*(?:.\d+)?)/
                regexResult = result.refRange.match(refRangeRegex)
                if(regexResult) {
                    let highBoundary = parseFloat(regexResult[1])
                    if(result.value <= highBoundary) {
                        return ""
                    }
                    return "HIGH"
                }
                refRangeRegex = />\s?([+-]?\d*(?:.\d+)?)/
                regexResult = result.refRange.match(refRangeRegex)
                if(regexResult) {
                    let highBoundary = parseFloat(regexResult[1])
                    if(result.value >= highBoundary) {
                        return ""
                    }
                    return "LOW"
                }
                return "?"
            },
            constructHcpcsSearch: function(query){
                if(!query) return "#"
                return "https://duckduckgo.com/?q=hcpcs+" + query.replace(' ', '+')
            },
            /**
             * addPanelResult allows the user to create a new lab panel result.
            */
            addPanelResult: function(){
                var id  = ObjectID();
                var newResult = {
                    id: id.toString(),
                    name: "",
                    refRange: "",
                    value: ""
                }
                if(!this.activePanel.results) this.activePanel.results = [];
                this.activePanel.results.push(newResult);
                this.saveSlide()
            },
            /**
             * deletePanelResult allows the user to delete a result from their lab panel
             * @param {int} resultIndex index of result to be deleted
            */
            deletePanelResult: function(resultIndex){
                this.activePanel.results.splice(resultIndex, 1);
                this.saveSlide()
            },
            /**
             * onPaste allows the user to paste a tab-formatted table and have it auto-entered
             * @param {value} resultIndex of values to be passed to function
             * @param {number} delay ms to delay in order for paste to complete
             */
            onPaste: function(resultIndex, delay=100){
                setTimeout(() => {
                    this.pasteExcel(resultIndex)
                }, delay)
            },
            /**
             * pasteExcel allows the user to paste a tab-formatted table and have it auto-entered
             * @param {int} resultIndex index of result pasted into
             */
            pasteExcel: function(resultIndex){
                var pastedString = this.activePanel.results[resultIndex].name
                if(pastedString && pastedString != ""){
                    var importedTable = ExcelImport.generateTable(pastedString, 3)
                    for(var i=0; i < importedTable.length; i++){
                        this.$set(this.activePanel.results, resultIndex+i, {
                            id: (new ObjectID).toString(),
                            name: importedTable[i][0],
                            refRange: importedTable[i][1],
                            value: importedTable[i][2]
                        })
                    }
                    this.saveSlide()
                }
            },
        },
        mounted: function(){
            this.$nextTick(function(){
                
            })
        }
    }
</script>

<style>
    .flag-cell{
        border-radius: 5px;
        display: inline-block;
        padding: 5px 8px;
        font-size: 85%;
    }
</style>
